import { zodResolver } from '@primevue/forms/resolvers/zod';
import { useNuxtApp } from 'nuxt/app';
import { useToast } from 'primevue/usetoast';
import { z } from 'zod';
import type { EnterOtpForm } from '~/types/auth/forms/ForgotPasswordForm';
import type { ApiInstance } from '~/types/plugins/api';
import { EnterOtp } from '~/utils/contents/auth/forms/ForgotPasswordForm';
import { $parseErrorMessage } from '~/utils/helpers';

export default function useSendForgotPasswordCode() {
  const loading = ref(false);
  const errors = ref<Record<string, string[]>>({});
  const form = ref<EnterOtpForm>(getInitialState());

  const toast = useToast();

  const validations = z.object({
    otp: z
      .union([z.string(), z.null(), z.undefined()])
      .transform((val) => val || '')
      .pipe(
        z
          .string()
          // NOTE: There's a problem with primevue otp input where
          // it returns just the first digit instead of the full value
          .min(1, {
            message: `${EnterOtp.fields.otp.label} must be valid`,
          }),
      ),
  });
  const resolver = zodResolver(validations);

  const submit = async (
    email: string,
    toastError: boolean = true,
  ): Promise<boolean> => {
    if (!validations.safeParse(form.value).success) {
      return false;
    }

    loading.value = true;

    try {
      const { $apiClient } = useNuxtApp() as unknown as {
        $apiClient: ApiInstance;
      };

      const result = await $apiClient.post<{
        error?: {
          data?: {
            errors?: Record<string, string[]>;
          };
        };
      }>('auth/reset-password/check', {
        username: email,
        token: form.value.otp,
      });

      if (result.error) {
        errors.value = result.error.data?.errors || {};
        return false;
      } else {
        return true;
      }
    } catch (error) {
      if (toastError) {
        toast.add({
          severity: 'error',
          summary: $parseErrorMessage(error),
          life: 3000,
        });
      } else {
        const message = $parseErrorMessage(error);
        if (message === 'This password reset token is invalid.') {
          errors.value = {
            otp: ['This code is invalid.'],
          };
        } else {
          errors.value = {
            otp: [message],
          };
        }
      }
      return false;
    } finally {
      loading.value = false;
    }
  };

  const reset = (): void => {
    form.value = getInitialState();
  };

  function getInitialState(): EnterOtpForm {
    return {
      otp: '',
    };
  }

  return {
    loading,
    form,
    errors,
    validations,
    resolver,

    submit,
    reset,
  };
}
